import { useCallback, useEffect, useRef } from "react"
import { useHistory } from "react-router-dom"

const LEAVE_MESSAGE = "Changes you made may not be saved. Are you sure you want to leave?"

/**
 * While `dirty`, warns before the tab is closed or reloaded, and before leaving the page inside
 * the app. Call the returned `release` before navigating away on purpose (after saving).
 */
const useUnsavedChangesGuard = (dirty: boolean) => {
  const history = useHistory()
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  const unblockRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return undefined

      const message = "Changes you made may not be saved."
      event.preventDefault()
      // eslint-disable-next-line no-param-reassign
      event.returnValue = message

      return message
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [])

  useEffect(() => {
    unblockRef.current = history.block(() => (dirtyRef.current ? LEAVE_MESSAGE : undefined))

    return () => {
      unblockRef.current?.()
      unblockRef.current = null
    }
  }, [history])

  return useCallback(() => {
    unblockRef.current?.()
    unblockRef.current = null
  }, [])
}

export default useUnsavedChangesGuard
